import { defineContent, type DefinedContent } from '@post-office/define-content';

export const registeredContent: DefinedContent<{
	default: [
		'heading',
		'body',
		'connectCalendar',
		'remindMe',
		'moreActions',
		'notInterested',
		'whyAmISeeingThis',
	];
	whyModal: [
		'title',
		'introduction',
		'teamworkCollection',
		'atlassianTools',
		'meetingWorkflow',
		'privacyLink',
		'close',
	];
	confirmations: [
		'reminderTitle',
		'reminderDescription',
		'notInterestedTitle',
		'notInterestedDescription',
		'notInterestedUndo',
	];
}> = defineContent({
	// `default` holds the side-nav banner copy; `role: 'title'` / `role: 'description'`
	// mark the two descriptors the offline content loader / reranker reads.
	default: {
		heading: {
			id: 'post-office.twc-loom-meeting-nudge-sidenav.heading',
			defaultMessage: 'Turn meetings into action',
			description: 'Heading for a side navigation banner promoting Loom meeting recordings',
			role: 'title',
		},
		body: {
			id: 'post-office.twc-loom-meeting-nudge-sidenav.body',
			defaultMessage:
				'Loom captures action items in Confluence and Jira so you don\u2019t have to.',
			description: 'Body copy for a side navigation banner promoting Loom meeting recordings',
			role: 'description',
		},
		connectCalendar: {
			id: 'post-office.twc-loom-meeting-nudge-sidenav.connect-calendar',
			defaultMessage: 'Connect calendar',
			description: "Action to open Loom's calendar connection flow",
		},
		remindMe: {
			id: 'post-office.twc-loom-meeting-nudge-sidenav.remind-me',
			defaultMessage: 'Remind me',
			description:
				'Action to defer the Loom calendar reminder until the next publish or live-page load',
		},
		moreActions: {
			id: 'post-office.twc-loom-meeting-nudge-sidenav.more-actions',
			defaultMessage: 'More actions',
			description: 'Label for the Loom meeting nudge options menu',
		},
		notInterested: {
			id: 'post-office.twc-loom-meeting-nudge-sidenav.not-interested',
			defaultMessage: 'Not interested',
			description: 'Action to permanently turn off Loom meeting recording suggestions',
		},
		whyAmISeeingThis: {
			id: 'post-office.twc-loom-meeting-nudge-sidenav.why-am-i-seeing-this',
			defaultMessage: 'Why am I seeing this?',
			description: 'Action to explain why the Loom meeting nudge was shown',
		},
	},
	// `whyModal` holds the "Why am I seeing this?" explanation modal copy.
	whyModal: {
		title: {
			id: 'post-office.twc-loom-meeting-nudge-sidenav.why-modal.title',
			defaultMessage: 'Why am I seeing this?',
			description: 'Title for an explanation of why the Loom meeting nudge was shown',
			role: 'title',
		},
		introduction: {
			id: 'post-office.twc-loom-meeting-nudge-sidenav.why-modal.introduction.ai-non-final',
			defaultMessage:
				'We think <strong>Loom</strong> will improve your team\u2019s productivity. The recommendation is based on:',
			description:
				'Introduction in the Loom meeting nudge explanation modal describing why Atlassian recommends Loom to the user',
			role: 'description',
		},
		teamworkCollection: {
			id: 'post-office.twc-loom-meeting-nudge-sidenav.why-modal.teamwork-collection.ai-non-final',
			defaultMessage: 'your access to the Teamwork Collection (which includes Loom)',
			description:
				'First reason in the Loom meeting nudge explanation modal, describing the user\u2019s access to the Teamwork Collection',
		},
		atlassianTools: {
			id: 'post-office.twc-loom-meeting-nudge-sidenav.why-modal.atlassian-tools.ai-non-final',
			defaultMessage: 'the tools you use (Confluence and Jira)',
			description:
				'Second reason in the Loom meeting nudge explanation modal, identifying the Atlassian tools the user already uses',
		},
		meetingWorkflow: {
			id: 'post-office.twc-loom-meeting-nudge-sidenav.why-modal.meeting-workflow.ai-non-final',
			defaultMessage: 'the way you manage meetings and documentation in your existing workflow',
			description:
				'Third reason in the Loom meeting nudge explanation modal, describing how the recommendation relates to the user\u2019s workflow',
		},
		privacyLink: {
			id: 'post-office.twc-loom-meeting-nudge-sidenav.why-modal.privacy-link.ai-non-final',
			defaultMessage: 'Learn more about your data privacy with Atlassian',
			description:
				'Link in the Loom meeting nudge explanation modal that opens Atlassian\u2019s privacy policy in a new browser tab',
		},
		close: {
			id: 'post-office.twc-loom-meeting-nudge-sidenav.why-modal.close.ai-non-final',
			defaultMessage: 'OK',
			description: 'Confirmation button that closes the Loom meeting nudge explanation modal',
		},
	},
	// `confirmations` holds the copy for the success flags shown after a user action.
	confirmations: {
		reminderTitle: {
			id: 'post-office.twc-loom-meeting-nudge-sidenav.reminder-confirmation.title',
			defaultMessage: 'We\u2019ll remind you again later',
			description: 'Title confirming that the Loom calendar connection reminder was deferred',
			role: 'title',
		},
		reminderDescription: {
			id: 'post-office.twc-loom-meeting-nudge-sidenav.reminder-confirmation.description',
			defaultMessage:
				'After you finish your doc, we\u2019ll remind you to connect your calendar to Loom.',
			description: 'Description explaining when the deferred Loom reminder will return',
			role: 'description',
		},
		notInterestedTitle: {
			id: 'post-office.twc-loom-meeting-nudge-sidenav.not-interested-confirmation.title',
			defaultMessage: 'Meeting recording suggestions turned off',
			description: 'Title confirming that Loom meeting recording suggestions were turned off',
		},
		notInterestedDescription: {
			id: 'post-office.twc-loom-meeting-nudge-sidenav.not-interested-confirmation.description',
			defaultMessage: 'You won\u2019t see meeting recording suggestions anymore.',
			description:
				'Description confirming that Loom meeting recording suggestions will no longer appear',
		},
		notInterestedUndo: {
			id: 'post-office.twc-loom-meeting-nudge-sidenav.not-interested-confirmation.undo',
			defaultMessage: 'Undo',
			description: 'Action to keep showing Loom meeting recording suggestions',
		},
	},
});
